import $ from 'jquery';
import { fabric } from 'fabric';
import { orderBy } from 'lodash';
import { triggerStateChangedRequest } from '~core';
import Pickr from '@simonwep/pickr/dist/pickr.es5.min';
import '@simonwep/pickr/dist/themes/monolith.min.css';

// Registry to link UI controls to Fabric gradients across renders
const gradientRegistry = new Map();
let gradientHandlersBound = false;

// Utilities to convert and format colors for UI
const clamp = (v, min = 0, max = 1) => Math.max(min, Math.min(max, v));
const hexToRgb = (hex) => {
  const res = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!res) return { r: 0, g: 0, b: 0 };
  return {
    r: parseInt(res[1], 16),
    g: parseInt(res[2], 16),
    b: parseInt(res[3], 16),
  };
};
const rgbaString = (hex, alpha = 1) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Normalize various color formats to { hex, alpha }
const normalizeColor = (color) => {
  if (!color) return { hex: '#000000', alpha: 1 };
  if (typeof color === 'string') {
    if (color.startsWith('#')) {
      let hex = color.toUpperCase();
      if (hex.length === 4) {
        hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
      }
      if (hex.length > 7) hex = hex.slice(0, 7);
      return { hex, alpha: 1 };
    } else if (color.startsWith('rgb(')) {
      const m = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (m) {
        const r = parseInt(m[1]);
        const g = parseInt(m[2]);
        const b = parseInt(m[3]);
        const hex =
          '#' +
          ((1 << 24) + (r << 16) + (g << 8) + b)
            .toString(16)
            .slice(1)
            .toUpperCase();
        return { hex, alpha: 1 };
      }
    } else if (color.startsWith('rgba(')) {
      const m = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([0-9.]+)\)/);
      if (m) {
        const r = parseInt(m[1]);
        const g = parseInt(m[2]);
        const b = parseInt(m[3]);
        const alpha = parseFloat(m[4]);
        const hex =
          '#' +
          ((1 << 24) + (r << 16) + (g << 8) + b)
            .toString(16)
            .slice(1)
            .toUpperCase();
        return { hex, alpha };
      }
    }
  }
  return { hex: color.toString().toUpperCase(), alpha: 1 };
};

// Ensure both Pickr instance and its swatch button reflect the given color
const setPickerSwatch = (gradientId, stopId, color, alpha = 1) => {
  const reg = gradientRegistry.get(gradientId);
  if (!reg || !reg.pickrs) return;
  const entry = reg.pickrs.get(stopId);
  if (!entry) return;
  const p = typeof entry.setColor === 'function' ? entry : entry.pickr;
  const elId = entry?.elId;
  const { hex } = normalizeColor(color || '#000000');
  const css = rgbaString(hex, alpha);
  try {
    if (p && typeof p.setColor === 'function') {
      p.setColor(css, true);
      if (typeof p.applyColor === 'function') p.applyColor(true);
    }
  } catch (e) {
    console.error('Failed setting pickr swatch color', e);
  }
  if (elId) {
    try {
      $(`#${elId} .pcr-button`).css('background', css);
    } catch (e) {
      console.error('Failed setting pickr swatch background', e);
    }
  }
};

const updateGradientPreview = (gradientId) => {
  const reg = gradientRegistry.get(gradientId);
  if (!reg) return;
  const grad = reg.obj.get(reg.property);
  if (!grad || !grad.colorStops) return;

  // Build a CSS preview (linear 90deg for visualization)
  const cssStops = [...grad.colorStops]
    .sort((a, b) => (a.offset || 0) - (b.offset || 0))
    .map((cs) => {
      const { hex } = normalizeColor(cs.color);
      const alpha = cs.opacity ?? 1;
      const pct = clamp(cs.offset, 0, 1) * 100;
      return `${rgbaString(hex, alpha)} ${pct}%`;
    })
    .join(', ');

  const $preview = $(`#gradient-preview-${gradientId}`);
  if ($preview.length) {
    $preview.css('background', `linear-gradient(90deg, ${cssStops})`);
  }
};

// Signature used to match gradients across reloads
const gradientSignature = (grad) => {
  if (!grad || !grad.colorStops) return null;
  const type = grad.type || 'linear';
  const coords = grad.coords || {};
  const stops = grad.colorStops.map((cs) => normalizeColor(cs.color).hex);
  return {
    type,
    stops,
    coords: Object.fromEntries(
      Object.entries(coords).map(([k, v]) => [
        k,
        typeof v === 'number' ? Math.round(v * 1000) / 1000 : v,
      ])
    ),
  };
};

// Persist current gradient overrides into MagicFrame.giotto.magicFrameSvgOverrides
const persistGradientOverride = (magicFrame, ownerTarget, obj, property) => {
  if (!magicFrame || !ownerTarget || !obj || !property) return;
  const grad = obj.get(property);
  if (!grad || !grad.colorStops) return;
  const original = grad._original || grad;
  const match = gradientSignature(original);
  if (!match) return;
  const apply = {
    stops: grad.colorStops.map((cs, i) => ({
      offset: clamp(
        cs.offset ?? i / Math.max(1, grad.colorStops.length - 1),
        0,
        1
      ),
      color: normalizeColor(cs.color).hex,
      opacity: cs.opacity,
    })),
  };
  const giotto = magicFrame.get('giotto') || {};
  const overrides = giotto.magicFrameSvgOverrides || {};
  const list = Array.isArray(overrides[ownerTarget])
    ? overrides[ownerTarget]
    : [];
  const existingIndex = list.findIndex((x) => {
    if (!x || x.property !== property || !x.match) return false;
    if (x.match.type !== match.type) return false;
    if (
      !Array.isArray(x.match.stops) ||
      x.match.stops.length !== match.stops.length
    )
      return false;
    for (let i = 0; i < x.match.stops.length; i++) {
      if (
        (x.match.stops[i] || '').toUpperCase() !==
        (match.stops[i] || '').toUpperCase()
      )
        return false;
    }
    return true;
  });
  const entry = { property, match, apply };
  if (existingIndex >= 0) list[existingIndex] = entry;
  else list.push(entry);
  const newOverrides = { ...overrides, [ownerTarget]: list };
  magicFrame.set('giotto', { ...giotto, magicFrameSvgOverrides: newOverrides });
};

const removeGradientOverride = (
  magicFrame,
  ownerTarget,
  obj,
  property,
  matchSnap
) => {
  if (!magicFrame || !ownerTarget) return;
  const giotto = magicFrame.get('giotto') || {};
  const overrides = giotto.magicFrameSvgOverrides || {};
  const list = Array.isArray(overrides[ownerTarget])
    ? overrides[ownerTarget]
    : [];
  const match = matchSnap ? gradientSignature(matchSnap) : null;
  if (!match) return;
  const newList = list.filter((x) => {
    if (!x || x.property !== property || !x.match) return true;
    if (x.match.type !== match.type) return true;
    if (
      !Array.isArray(x.match.stops) ||
      x.match.stops.length !== match.stops.length
    )
      return true;
    for (let i = 0; i < x.match.stops.length; i++) {
      if (
        (x.match.stops[i] || '').toUpperCase() !==
        (match.stops[i] || '').toUpperCase()
      )
        return true;
    }
    return false;
  });
  const newOverrides = { ...overrides, [ownerTarget]: newList };
  magicFrame.set('giotto', { ...giotto, magicFrameSvgOverrides: newOverrides });
};

// Take a serializable snapshot of a Fabric gradient (moved up for linting)
const snapshotGradient = (grad) => ({
  type: grad.type || 'linear',
  gradientUnits: grad.gradientUnits,
  gradientTransform: grad.gradientTransform,
  coords: { ...(grad.coords || {}) },
  colorStops: (grad.colorStops || []).map((cs, i) => ({
    offset: clamp(
      cs.offset ?? i / Math.max(1, grad.colorStops.length - 1),
      0,
      1
    ),
    color: cs.color,
    opacity: cs.opacity,
    _magicStopId: cs._magicStopId || `orig-stop-${i}`,
  })),
});

// Clone a Fabric gradient preserving metadata and applying a transformer
const rebuildAndApplyGradient = (obj, property, transformer) => {
  const grad = obj.get(property);
  if (!grad || !grad.colorStops) return false;
  // Ensure original snapshot is stored at first edit
  if (!grad._original) {
    grad._original = snapshotGradient(grad);
  }
  const newStops = grad.colorStops.map((cs, i) => ({
    offset: cs.offset,
    color: cs.color,
    opacity: cs.opacity,
    _i: i,
    _magicStopId: cs._magicStopId,
  }));
  const transformedStops = transformer
    ? transformer(newStops) || newStops
    : newStops;
  // Clamp and sort stops by offset to keep gradient stable
  transformedStops.forEach((s) => {
    s.offset = clamp(s.offset, 0, 1);
  });
  const sortedStops = orderBy(transformedStops, ['offset'], ['asc']);
  const newGradient = new fabric.Gradient({
    type: grad.type || 'linear',
    gradientUnits: grad.gradientUnits,
    gradientTransform: grad.gradientTransform,
    coords: grad.coords,
    colorStops: sortedStops.map(({ offset, color, opacity, _magicStopId }) => ({
      offset,
      color,
      opacity,
      _magicStopId,
    })),
  });
  // Re-attach our stop IDs in case fabric strips unknown fields
  if (Array.isArray(newGradient.colorStops)) {
    newGradient.colorStops.forEach((cs, i) => {
      cs._magicStopId =
        sortedStops[i]?._magicStopId ?? cs._magicStopId ?? `stop-${i}`;
    });
  }
  // Preserve original snapshot on the new gradient
  newGradient._original = grad._original;
  obj.set(property, newGradient);
  obj.dirty = true;
  return true;
};

// snapshotGradient defined earlier

// Apply a snapshot back to an object property as a Fabric gradient
const applyGradientSnapshot = (obj, property, snap) => {
  if (!snap) return false;
  const newGradient = new fabric.Gradient({
    type: snap.type || 'linear',
    gradientUnits: snap.gradientUnits,
    gradientTransform: snap.gradientTransform,
    coords: snap.coords,
    colorStops: (snap.colorStops || []).map(
      ({ offset, color, opacity, _magicStopId }) => ({
        offset,
        color,
        opacity,
        _magicStopId,
      })
    ),
  });
  if (Array.isArray(newGradient.colorStops)) {
    newGradient.colorStops.forEach((cs, i) => {
      cs._magicStopId =
        (snap.colorStops || [])[i]?._magicStopId ||
        cs._magicStopId ||
        `stop-${i}`;
    });
  }
  // Keep original for future resets
  newGradient._original = { ...snap, colorStops: [...(snap.colorStops || [])] };
  obj.set(property, newGradient);
  obj.dirty = true;
  return true;
};

const createMagicFrameColorPicker = (
  id,
  initialHexColor,
  object,
  property,
  parent
) => {
  const pickr = Pickr.create({
    el: `#${id}`,

    theme: 'monolith',

    comparison: false,

    default: initialHexColor,

    swatches: [
      'rgba(0,0,0, 1)',
      'rgba(130,130,130, 1)',
      'rgba(255,255,255, 1)',
      'rgba(255,255,255, 0)',
      'rgba(188,160,76, 1)',
      'rgba(148,23,81, 1)',
      'rgba(224,41,17, 1)',
      'rgba(255,147,0, 1)',
      'rgba(255,252,121, 1)',
      'rgba(79,143,0, 1)',
      'rgba(0,145,147, 1)',
      'rgba(0,84,147, 1)',
      'rgba(4,51,255, 1)',
      'rgba(131,68,204, 1)',
    ],

    components: {
      // Main components
      preview: true,
      opacity: true,
      hue: true,

      // Input / output Options
      interaction: {
        input: true,
      },
    },
  });

  pickr.on('change', (color) => {
    const hexaArr = color.toHEXA();
    const hex = `#${hexaArr.slice(0, 3).join('').toUpperCase()}`; // ensure #RRGGBB
    const rgba = color.toRGBA();
    const alpha = rgba[3];

    if (property === 'fill') {
      if (parent && parent.type === 'gradient-stop') {
        // Rebuild gradient preserving metadata
        const reg = parent.gradientId
          ? gradientRegistry.get(parent.gradientId)
          : null;
        const obj = reg?.obj || parent.obj;
        let affectedStopIds = [];
        const changed = rebuildAndApplyGradient(obj, 'fill', (stops) => {
          const idx = stops.findIndex((s) => s._magicStopId === parent.stopId);
          if (idx === -1) return stops;
          const { hex: oldHex } = normalizeColor(stops[idx].color || '#000000');
          const linkSame = parent.gradientId
            ? gradientRegistry.get(parent.gradientId)?.linkSame ?? true
            : false;
          if (linkSame) {
            return stops.map((s) => {
              const { hex: sHex } = normalizeColor(s.color || '#000000');
              if (sHex.toUpperCase() === oldHex.toUpperCase()) {
                affectedStopIds.push(s._magicStopId);
                return { ...s, color: hex, opacity: alpha };
              }
              return s;
            });
          }
          affectedStopIds = [parent.stopId];
          stops[idx].color = hex;
          stops[idx].opacity = alpha;
          return stops;
        });
        if (!changed) {
          object.set({ fill: hex });
        } else {
          try {
            persistGradientOverride(
              parent.magicFrame,
              parent.ownerTarget,
              obj,
              'fill'
            );
          } catch (e) {
            console.error('Failed persisting gradient override (fill)', e);
          }
          // Update other linked stop pickers silently to reflect new color
          const regEntry = gradientRegistry.get(parent.gradientId);
          if (regEntry && regEntry.pickrs && affectedStopIds.length) {
            affectedStopIds.forEach((sid) => {
              if (sid === parent.stopId) return;
              setPickerSwatch(parent.gradientId, sid, hex, alpha);
            });
          }
        }
      } else {
        // Handle solid fill
        object.set({ fill: hex });
      }
    } else if (property === 'stroke') {
      if (parent && parent.type === 'stroke-gradient-stop') {
        // Rebuild gradient preserving metadata
        const reg = parent.gradientId
          ? gradientRegistry.get(parent.gradientId)
          : null;
        const obj = reg?.obj || parent.obj;
        let affectedStopIds = [];
        const changed = rebuildAndApplyGradient(obj, 'stroke', (stops) => {
          const idx = stops.findIndex((s) => s._magicStopId === parent.stopId);
          if (idx === -1) return stops;
          const { hex: oldHex } = normalizeColor(stops[idx].color || '#000000');
          const linkSame = parent.gradientId
            ? gradientRegistry.get(parent.gradientId)?.linkSame ?? true
            : false;
          if (linkSame) {
            return stops.map((s) => {
              const { hex: sHex } = normalizeColor(s.color || '#000000');
              if (sHex.toUpperCase() === oldHex.toUpperCase()) {
                affectedStopIds.push(s._magicStopId);
                return { ...s, color: hex, opacity: alpha };
              }
              return s;
            });
          }
          affectedStopIds = [parent.stopId];
          stops[idx].color = hex;
          stops[idx].opacity = alpha;
          return stops;
        });
        if (!changed) {
          object.set({ stroke: hex });
        } else {
          try {
            persistGradientOverride(
              parent.magicFrame,
              parent.ownerTarget,
              obj,
              'stroke'
            );
          } catch (e) {
            console.error('Failed persisting gradient override (stroke)', e);
          }
          const regEntry = gradientRegistry.get(parent.gradientId);
          if (regEntry && regEntry.pickrs && affectedStopIds.length) {
            affectedStopIds.forEach((sid) => {
              if (sid === parent.stopId) return;
              setPickerSwatch(parent.gradientId, sid, hex, alpha);
            });
          }
        }
      } else {
        // Handle solid stroke
        object.set({ stroke: hex });
      }
    } else if (property === 'colorStop') {
      object.color = hex;
      object.opacity = alpha;
      parent.set({ dirty: true });
    }

    window.canvas.renderAll();
    // Update visual preview for gradients if present
    if (parent && parent.gradientId) {
      updateGradientPreview(parent.gradientId);
    }
    triggerStateChangedRequest();
  });

  return pickr;
};

// rgbToRgba helper removed (unused)

// Helper function to traverse SVG objects
const traverse = (obj, callback) => {
  callback(obj);
  if (obj.getObjects && typeof obj.getObjects === 'function') {
    obj.getObjects().forEach((child) => traverse(child, callback));
  }
};

// Improved color extraction function that groups gradients
const extractColorsFromSvgGroup = (group, options = {}) => {
  const solidColors = [];
  const gradients = [];
  const { magicFrame = null, target = null } = options;

  traverse(group, (o) => {
    // Handle solid fill colors
    if (o.fill && typeof o.fill === 'string') {
      const { hex, alpha } = normalizeColor(o.fill);
      const sanitizedId = `magicframe-fill-${hex.replace(
        /[^a-zA-Z0-9]/g,
        '-'
      )}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      solidColors.push({
        id: sanitizedId,
        hex:
          alpha < 1
            ? `${hex}${Math.round(alpha * 255)
                .toString(16)
                .padStart(2, '0')
                .toUpperCase()}`
            : hex,
        object: o,
        property: 'fill',
        ref: { type: 'fill', obj: o, alpha },
      });
    }

    // Handle fill gradients
    if (o.fill && o.fill.colorStops) {
      const gradientId = `gradient-fill-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      const gradientStops = [];

      // Ensure original snapshot is stored
      if (!o.fill._original) {
        o.fill._original = snapshotGradient(o.fill);
      }

      o.fill.colorStops.forEach((cs, idx) => {
        const { hex, alpha } = normalizeColor(cs.color);
        const stopAlpha = cs.opacity ?? alpha;
        const sanitizedId = `magicframe-${gradientId}-stop-${idx}`;
        // Attach a stable ID to the Fabric colorStop so we can find it after reorders
        if (!cs._magicStopId) cs._magicStopId = sanitizedId;

        gradientStops.push({
          id: sanitizedId,
          hex:
            stopAlpha < 1
              ? `${hex}${Math.round(stopAlpha * 255)
                  .toString(16)
                  .padStart(2, '0')
                  .toUpperCase()}`
              : hex,
          object: o,
          property: 'fill',
          ref: {
            type: 'gradient-stop',
            gradient: o.fill,
            index: idx,
            obj: o,
            alpha: stopAlpha,
            stopId: cs._magicStopId,
            magicFrame,
            ownerTarget: target,
          },
          offset: cs.offset || idx / (o.fill.colorStops.length - 1),
        });
      });

      gradients.push({
        id: gradientId,
        type: o.fill.type || 'linear',
        property: 'fill',
        stops: gradientStops,
      });
    }

    // Handle solid stroke colors
    if (o.stroke && typeof o.stroke === 'string') {
      const { hex, alpha } = normalizeColor(o.stroke);
      const sanitizedId = `magicframe-stroke-${hex.replace(
        /[^a-zA-Z0-9]/g,
        '-'
      )}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      solidColors.push({
        id: sanitizedId,
        hex:
          alpha < 1
            ? `${hex}${Math.round(alpha * 255)
                .toString(16)
                .padStart(2, '0')
                .toUpperCase()}`
            : hex,
        object: o,
        property: 'stroke',
        ref: { type: 'stroke', obj: o, alpha },
      });
    }

    // Handle stroke gradients
    if (o.stroke && o.stroke.colorStops) {
      const gradientId = `gradient-stroke-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      const gradientStops = [];

      // Ensure original snapshot is stored
      if (!o.stroke._original) {
        o.stroke._original = snapshotGradient(o.stroke);
      }

      o.stroke.colorStops.forEach((cs, idx) => {
        const { hex, alpha } = normalizeColor(cs.color);
        const stopAlpha = cs.opacity ?? alpha;
        const sanitizedId = `magicframe-${gradientId}-stop-${idx}`;
        // Attach a stable ID to the Fabric colorStop so we can find it after reorders
        if (!cs._magicStopId) cs._magicStopId = sanitizedId;

        gradientStops.push({
          id: sanitizedId,
          hex:
            stopAlpha < 1
              ? `${hex}${Math.round(stopAlpha * 255)
                  .toString(16)
                  .padStart(2, '0')
                  .toUpperCase()}`
              : hex,
          object: o,
          property: 'stroke',
          ref: {
            type: 'stroke-gradient-stop',
            gradient: o.stroke,
            index: idx,
            obj: o,
            alpha: stopAlpha,
            stopId: cs._magicStopId,
            magicFrame,
            ownerTarget: target,
          },
          offset: cs.offset || idx / (o.stroke.colorStops.length - 1),
        });
      });

      gradients.push({
        id: gradientId,
        type: o.stroke.type || 'linear',
        property: 'stroke',
        stops: gradientStops,
      });
    }
  });

  console.log('Extracted solid colors:', solidColors);
  console.log('Extracted gradients:', gradients);

  return { solidColors, gradients };
};

export const handleMagicFrameBackgroundColors = (magicFrame) => {
  const $container = $('.magicframe-bg-colors');
  $container.empty();

  if (!magicFrame.backgroundSVG) {
    return;
  }

  const { solidColors, gradients } = extractColorsFromSvgGroup(
    magicFrame.backgroundSVG,
    {
      magicFrame,
      target: 'background',
    }
  );

  const totalColors =
    solidColors.length + gradients.reduce((sum, g) => sum + g.stops.length, 0);
  if (totalColors === 0 || totalColors > 20) {
    if (totalColors > 20) {
      console.log(
        'Too many background SVG colors, skipping pickers',
        totalColors
      );
    }
    return;
  }

  // Title is provided by the floating control header; avoid duplicating here

  // Render solid colors
  if (solidColors.length > 0) {
    for (const color of solidColors) {
      $container.append(
        `<div id="${color.id}" class="d-inline-block m-1"></div>`
      );
      createMagicFrameColorPicker(
        color.id,
        color.hex,
        color.object,
        color.property,
        color.ref
      );
    }
  }

  // Render gradients with visual grouping
  if (gradients.length > 0) {
    for (const gradient of gradients) {
      const gradientLabel =
        gradient.type === 'radial' ? 'Sfumatura Radiale' : 'Sfumatura Lineare';
      const groupId = `gradient-group-${gradient.id}`;
      const previewId = `gradient-preview-${gradient.id}`;
      const cssPreview = gradient.stops
        .map((s) => `${s.hex} ${Math.round(s.offset * 100)}%`)
        .join(', ');

      // Register this gradient for later updates
      if (gradient.stops.length > 0) {
        const objForGrad = gradient.stops[0].object;
        const gradObj = objForGrad.get
          ? objForGrad.get(gradient.property)
          : null;
        if (gradObj && !gradObj._original)
          gradObj._original = snapshotGradient(gradObj);
        gradientRegistry.set(gradient.id, {
          obj: gradient.stops[0].object,
          property: gradient.property,
          groupId,
          previewId,
          linkSame: true,
          original: gradObj?._original || null,
        });
      }

      $container.append(`
        <div id="${groupId}" class="gradient-group magicframe-gradient mt-2 p-2 border rounded">
          <div class="d-flex align-items-center justify-content-between mb-1">
            <small class="text-dark font-weight-bold">${gradientLabel}</small>
            <div class="form-check form-check-inline m-0">
              <input class="form-check-input" type="checkbox" id="link-same-colors-${
                gradient.id
              }" data-gradient-link-same data-gradient-id="${
        gradient.id
      }" checked>
              <label class="form-check-label" for="link-same-colors-${
                gradient.id
              }"><small>Modifica tutti i colori uguali</small></label>
            </div>
          </div>
          <div id="${previewId}" class="w-100 mb-2" style="height:14px;border-radius:8px;background: linear-gradient(90deg, ${cssPreview});"></div>
          <div class="gradient-stops d-flex flex-wrap">
            ${gradient.stops
              .map(
                (stop, idx) => `
              <div class="gradient-stop-wrapper d-flex align-items-center mr-2 mb-2">
                <div id="${stop.id}" class="d-inline-block mr-2"></div>
                <input type="range" min="0" max="100" value="${Math.round(
                  stop.offset * 100
                )}" data-gradient-stop-offset data-gradient-id="${
                  gradient.id
                }" data-stop-index="${idx}" data-stop-id="${
                  stop.ref.stopId
                }" class="custom-range" style="width:120px;">
                <small class="ml-2 text-dark" data-stop-label="${gradient.id}:${
                  stop.ref.stopId
                }">${Math.round(stop.offset * 100)}%</small>
              </div>
              `
              )
              .join('')}
          </div>
          <div class="d-flex justify-content-end mt-2">
            <button type="button" class="btn btn-sm btn-light border" data-gradient-reset data-gradient-id="${
              gradient.id
            }">Ripristina colori originali</button>
          </div>
        </div>
      `);

      // Create color pickers for each gradient stop
      for (const stop of gradient.stops) {
        // Enrich stop ref with UI linkage
        stop.ref.gradientId = gradient.id;
        stop.ref.groupId = groupId;
        const picker = createMagicFrameColorPicker(
          stop.id,
          stop.hex,
          stop.object,
          stop.property,
          stop.ref
        );
        const reg = gradientRegistry.get(gradient.id);
        if (reg) {
          if (!reg.pickrs) reg.pickrs = new Map();
          reg.pickrs.set(stop.ref.stopId, { pickr: picker, elId: stop.id });
        }
      }
      // Initialize preview to ensure consistency
      updateGradientPreview(gradient.id);
    }
  }
};

export const handleMagicFrameFrameColors = (magicFrame) => {
  const $container = $('.magicframe-frame-colors');
  $container.empty();

  if (!magicFrame.frameSVG) {
    return;
  }

  const { solidColors, gradients } = extractColorsFromSvgGroup(
    magicFrame.frameSVG,
    {
      magicFrame,
      target: 'frame',
    }
  );

  const totalColors =
    solidColors.length + gradients.reduce((sum, g) => sum + g.stops.length, 0);
  if (totalColors === 0 || totalColors > 20) {
    if (totalColors > 20) {
      console.log('Too many frame SVG colors, skipping pickers', totalColors);
    }
    return;
  }

  // Title is provided by the floating control header; avoid duplicating here

  // Render solid colors
  if (solidColors.length > 0) {
    for (const color of solidColors) {
      $container.append(
        `<div id="${color.id}" class="d-inline-block m-1"></div>`
      );
      createMagicFrameColorPicker(
        color.id,
        color.hex,
        color.object,
        color.property,
        color.ref
      );
    }
  }

  // Render gradients with visual grouping
  if (gradients.length > 0) {
    for (const gradient of gradients) {
      const gradientLabel =
        gradient.type === 'radial' ? 'Sfumatura Radiale' : 'Sfumatura Lineare';
      const groupId = `gradient-group-${gradient.id}`;
      const previewId = `gradient-preview-${gradient.id}`;
      const cssPreview = gradient.stops
        .map((s) => `${s.hex} ${Math.round(s.offset * 100)}%`)
        .join(', ');

      // Register this gradient for later updates
      if (gradient.stops.length > 0) {
        const objForGrad = gradient.stops[0].object;
        const gradObj = objForGrad.get
          ? objForGrad.get(gradient.property)
          : null;
        if (gradObj && !gradObj._original)
          gradObj._original = snapshotGradient(gradObj);
        gradientRegistry.set(gradient.id, {
          obj: gradient.stops[0].object,
          property: gradient.property,
          groupId,
          previewId,
          linkSame: true,
          original: gradObj?._original || null,
        });
      }

      $container.append(`
        <div id="${groupId}" class="gradient-group magicframe-gradient mt-2 p-2 border rounded">
          <div class="d-flex align-items-center justify-content-between mb-1">
            <small class="text-dark font-weight-bold">${gradientLabel}</small>
            <div class="form-check form-check-inline m-0">
              <input class="form-check-input" type="checkbox" id="link-same-colors-${
                gradient.id
              }" data-gradient-link-same data-gradient-id="${
        gradient.id
      }" checked>
              <label class="form-check-label" for="link-same-colors-${
                gradient.id
              }"><small>Modifica tutti i colori uguali</small></label>
            </div>
          </div>
          <div id="${previewId}" class="w-100 mb-2" style="height:14px;border-radius:8px;background: linear-gradient(90deg, ${cssPreview});"></div>
          <div class="gradient-stops d-flex flex-wrap">
            ${gradient.stops
              .map(
                (stop, idx) => `
              <div class="gradient-stop-wrapper d-flex align-items-center mr-2 mb-2">
                <div id="${stop.id}" class="d-inline-block mr-2"></div>
                <input type="range" min="0" max="100" value="${Math.round(
                  stop.offset * 100
                )}" data-gradient-stop-offset data-gradient-id="${
                  gradient.id
                }" data-stop-index="${idx}" data-stop-id="${
                  stop.ref.stopId
                }" class="custom-range" style="width:120px;">
                <small class="ml-2 text-dark" data-stop-label="${gradient.id}:${
                  stop.ref.stopId
                }">${Math.round(stop.offset * 100)}%</small>
              </div>
            `
              )
              .join('')}
          </div>
          <div class="d-flex justify-content-end mt-2">
            <button type="button" class="btn btn-sm btn-light border" data-gradient-reset data-gradient-id="${
              gradient.id
            }">Ripristina colori originali</button>
          </div>
        </div>
      `);

      // Create color pickers for each gradient stop
      for (const stop of gradient.stops) {
        // Enrich stop ref with UI linkage
        stop.ref.gradientId = gradient.id;
        stop.ref.groupId = groupId;
        const picker = createMagicFrameColorPicker(
          stop.id,
          stop.hex,
          stop.object,
          stop.property,
          stop.ref
        );
        const reg = gradientRegistry.get(gradient.id);
        if (reg) {
          if (!reg.pickrs) reg.pickrs = new Map();
          reg.pickrs.set(stop.ref.stopId, { pickr: picker, elId: stop.id });
        }
      }
      // Initialize preview to ensure consistency
      updateGradientPreview(gradient.id);
    }
  }
};

export const handleMagicFrameSvgColors = (magicFrame) => {
  // Reset registry for a fresh UI build
  gradientRegistry.clear();
  // Bind UI handlers once
  if (!gradientHandlersBound) {
    gradientHandlersBound = true;
    // Adjust stop offset via range sliders
    $(document).on('input change', '[data-gradient-stop-offset]', function () {
      const $el = $(this);
      const gradientId = $el.data('gradient-id');
      const stopIndex = parseInt($el.data('stop-index'), 10);
      const stopId = $el.data('stop-id');
      const reg = gradientRegistry.get(gradientId);
      if (!reg) return;
      const obj = reg.obj;
      const grad = obj.get(reg.property);
      if (!grad || !grad.colorStops) return;
      const offset = clamp(parseInt($el.val(), 10) / 100);
      rebuildAndApplyGradient(obj, reg.property, (stops) => {
        const idx =
          typeof stopId !== 'undefined'
            ? stops.findIndex((s) => s._magicStopId === stopId)
            : stopIndex;
        if (stops[idx]) stops[idx].offset = offset;
        return stops;
      });
      window.canvas.renderAll();
      // Update label and preview
      $(`[data-stop-label="${gradientId}:${stopId}"]`).text(
        `${Math.round(offset * 100)}%`
      );
      updateGradientPreview(gradientId);
      triggerStateChangedRequest();
      // Persist offset override to metadata
      try {
        const mf = window.canvas
          .getObjects()
          .find(
            (o) =>
              o.type === 'MagicFrame' &&
              (o.backgroundSVG === obj.group || o.frameSVG === obj.group)
          );
        const ownerTarget = mf
          ? mf.backgroundSVG === obj.group
            ? 'background'
            : mf.frameSVG === obj.group
            ? 'frame'
            : null
          : null;
        if (mf && ownerTarget) {
          persistGradientOverride(mf, ownerTarget, obj, reg.property);
        }
      } catch (e) {
        console.error('Failed removing persisted gradient override', e);
      }
    });

    // Toggle "link same colors" behavior per gradient
    $(document).on('change', '[data-gradient-link-same]', function () {
      const $el = $(this);
      const gradientId = $el.data('gradient-id');
      const reg = gradientRegistry.get(gradientId);
      if (reg) {
        reg.linkSame = $el.is(':checked');
      }
    });

    // Reset gradient colors to original snapshot
    $(document).on('click', '[data-gradient-reset]', function () {
      const gradientId = $(this).data('gradient-id');
      const reg = gradientRegistry.get(gradientId);
      if (!reg) return;
      const obj = reg.obj;
      const grad = obj.get(reg.property);
      const snap = grad?._original || reg.original || null;
      if (!snap) return;
      // Apply snapshot back to object
      if (!applyGradientSnapshot(obj, reg.property, snap)) return;
      // Remove any persisted override for this gradient signature
      try {
        const mf = window.canvas
          .getObjects()
          .find(
            (o) =>
              o.type === 'MagicFrame' &&
              (o.backgroundSVG === obj.group || o.frameSVG === obj.group)
          );
        const ownerTarget = mf
          ? mf.backgroundSVG === obj.group
            ? 'background'
            : mf.frameSVG === obj.group
            ? 'frame'
            : null
          : null;
        if (mf && ownerTarget) {
          removeGradientOverride(mf, ownerTarget, obj, reg.property, snap);
        }
      } catch (e) {
        console.error(
          'Failed re-rendering MagicFrame colors UI after reset',
          e
        );
      }
      // Update pickrs and sliders
      // pickrs map is available via reg.pickrs if needed
      (snap.colorStops || []).forEach(
        ({ _magicStopId, color, opacity, offset }) => {
          const { hex: nx, alpha: na } = normalizeColor(color || '#000000');
          const a = opacity ?? na ?? 1;
          setPickerSwatch(gradientId, _magicStopId, nx, a);
          // Update slider and label
          const pct = Math.round(clamp(offset, 0, 1) * 100);
          $(
            `input[data-gradient-stop-offset][data-gradient-id="${gradientId}"][data-stop-id="${_magicStopId}"]`
          ).val(pct);
          $(`[data-stop-label="${gradientId}:${_magicStopId}"]`).text(
            `${pct}%`
          );
        }
      );
      window.canvas.renderAll();
      updateGradientPreview(gradientId);
      triggerStateChangedRequest();
      // As a robust fallback, rebuild the whole Magic Frame colors UI after reset
      try {
        const {
          getActiveMagicFrameActive,
        } = require('~core/magicFrame/getActiveMagicFrameActive');
        const magicFrame =
          getActiveMagicFrameActive && getActiveMagicFrameActive();
        if (magicFrame) {
          // Re-render both sections to ensure perfect sync
          handleMagicFrameSvgColors(magicFrame);
        }
      } catch (e) {
        console.error('Failed rebuilding MagicFrame colors UI after reset', e);
      }
    });
  }
  handleMagicFrameBackgroundColors(magicFrame);
  handleMagicFrameFrameColors(magicFrame);
};
